import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  Users,
  Mail,
  UploadCloud,
  Search,
  Layers,
  Check,
  Copy,
  RefreshCw,
  Trash2,
  Building2,
  Sparkles,
  UserCheck,
  BrainCircuit,
  AlertTriangle,
  TrendingUp,
  Clock,
} from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type { B2bMembershipRequestItem } from "@/src/api/types.gen";
import type { OrgStats, MemberRow, InvitationItem, WorkspaceItem } from "../types";
import { getErrorMessage } from "../types";
import { EmployeeDossierDrawer } from "../components/EmployeeDossierDrawer";
import {
  InviteMemberModal,
  BulkInviteModal,
  Modal,
  ApproveRequestModal,
  ConfirmActionModal,
  type ConfirmAction,
} from "../components/modals";

export interface MembersPageProps {
  slug: string;
  stats: OrgStats;
  members: MemberRow[];
  invitations: InvitationItem[];
  workspaces: WorkspaceItem[];
  loadMembers: (force?: boolean) => Promise<void>;
  loadInvites: (force?: boolean) => Promise<void>;
  loadStats?: (force?: boolean) => Promise<void>;
}

export const MembersPage: React.FC<MembersPageProps> = ({
  slug,
  stats,
  members,
  invitations,
  workspaces,
  loadMembers,
  loadInvites,
  loadStats,
}) => {
  const { user: clerkUser } = useUser();
  const [membersSubTab, setMembersSubTab] = useState<"members" | "requests" | "invites">("members");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDeptFilter, setSelectedDeptFilter] = useState<string>("All");

  // Membership request states
  const [pendingRequests, setPendingRequests] = useState<B2bMembershipRequestItem[]>([]);
  const [, setLoadingRequests] = useState<boolean>(false);
  const [processingRequestId, setProcessingRequestId] = useState<number | null>(null);

  // Invite action states
  const [copiedInviteId, setCopiedInviteId] = useState<number | null>(null);
  const [resendingInviteId, setResendingInviteId] = useState<number | null>(null);
  const [revokingInviteId, setRevokingInviteId] = useState<number | null>(null);

  // Member removal states
  const [memberToRemove, setMemberToRemove] = useState<MemberRow | null>(null);
  const [isRemovingMember, setIsRemovingMember] = useState<boolean>(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const adminCount = useMemo(() => {
    return members.filter((m) => m.role?.toLowerCase() === "admin").length;
  }, [members]);

  // Modal triggers
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [requestToApprove, setRequestToApprove] = useState<B2bMembershipRequestItem | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  // Early-warning filter and dossier drawer state
  const [statusFilterPill, setStatusFilterPill] = useState<"all" | "gaps" | "deadline" | "overdue" | "top_performer">("all");
  const [selectedDossierMember, setSelectedDossierMember] = useState<MemberRow | null>(null);

  // Early-warning metric counts
  const atRiskCount = useMemo(
    () => members.filter((m) => (m.gaps_count && m.gaps_count > 0) || m.at_risk || m.status_pill === "gaps_detected").length,
    [members]
  );
  const deadlineCount = useMemo(
    () => members.filter((m) => m.status_pill === "approaching_deadline").length,
    [members]
  );
  const overdueCount = useMemo(
    () => members.filter((m) => (m.overdue_count && m.overdue_count > 0) || m.status_pill === "overdue").length,
    [members]
  );
  const topPerformerCount = useMemo(
    () => members.filter((m) => m.status_pill === "top_performer").length,
    [members]
  );

  // Filtered active members
  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const matchesSearch =
        searchQuery.trim() === "" ||
        (m.name && m.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (m.email && m.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (m.role && m.role.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (m.job_title && m.job_title.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesDept =
        selectedDeptFilter === "All" ||
        (m.department && m.department.toLowerCase() === selectedDeptFilter.toLowerCase());

      const matchesStatus =
        statusFilterPill === "all" ||
        (statusFilterPill === "gaps" && ((m.gaps_count && m.gaps_count > 0) || m.at_risk || m.status_pill === "gaps_detected")) ||
        (statusFilterPill === "deadline" && m.status_pill === "approaching_deadline") ||
        (statusFilterPill === "overdue" && ((m.overdue_count && m.overdue_count > 0) || m.status_pill === "overdue")) ||
        (statusFilterPill === "top_performer" && m.status_pill === "top_performer");

      return matchesSearch && matchesDept && matchesStatus;
    });
  }, [members, searchQuery, selectedDeptFilter, statusFilterPill]);

  // Filtered invitations
  const filteredInvitations = useMemo(() => {
    return invitations.filter((inv) => {
      const matchesSearch =
        searchQuery.trim() === "" ||
        inv.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (inv.job_title && inv.job_title.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesDept =
        selectedDeptFilter === "All" ||
        (inv.department && inv.department.toLowerCase() === selectedDeptFilter.toLowerCase());

      return matchesSearch && matchesDept;
    });
  }, [invitations, searchQuery, selectedDeptFilter]);

  const handleCopyInviteLink = (invite: InvitationItem) => {
    const url = invite.invite_url || `${window.location.origin}/invitations/accept?token=${invite.uuid}`;
    navigator.clipboard.writeText(url);
    setCopiedInviteId(invite.id);
    setTimeout(() => setCopiedInviteId(null), 2500);
  };

  const handleResendInvite = async (inviteId: number) => {
    if (!slug) return;
    try {
      setResendingInviteId(inviteId);
      const { data, error } = await api.resendInvitationApiV1B2bSlugInvitesInviteIdResendPost({
        path: { slug, invite_id: inviteId },
      });
      if (data) {
        alert("Invitation email resent successfully!");
      } else {
        alert(`Failed to resend invitation: ${getErrorMessage(error, "Unknown error")}`);
      }
    } catch (err) {
      console.error("Failed to resend invite:", err);
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleRevokeInvite = (inviteId: number) => {
    if (!slug) return;
    const invitation = invitations.find((item) => item.id === inviteId);
    setConfirmAction({
      title: "Revoke invitation?",
      description: `The invitation to ${invitation?.email || "this user"} will be invalidated. They will no longer be able to join using this invite.`,
      confirmLabel: "Revoke invitation",
      onConfirm: async () => {
        try {
          setRevokingInviteId(inviteId);
          const { error } = await api.revokeInvitationApiV1B2bSlugInvitesInviteIdDelete({
            path: { slug, invite_id: inviteId },
          });
          if (!error) {
            await loadInvites(true);
            if (loadStats) await loadStats(true);
          } else {
            alert(`Failed to revoke invitation: ${getErrorMessage(error, "Unknown error")}`);
          }
        } catch (err) {
          console.error("Failed to revoke invite:", err);
        } finally {
          setRevokingInviteId(null);
        }
      },
    });
  };

  const handleConfirmRemoveMember = async () => {
    if (!slug || !memberToRemove) return;
    try {
      setIsRemovingMember(true);
      setRemoveError(null);
      const { error } = await api.removeOrgMemberApiV1B2bSlugMembersUserIdDelete({
        path: { slug, user_id: memberToRemove.user_id },
      });
      if (error) {
        setRemoveError(getErrorMessage(error, "Failed to remove member"));
        return;
      }
      setMemberToRemove(null);
      await loadMembers(true);
      if (loadStats) await loadStats(true);
    } catch (err: any) {
      console.error("Failed to remove member:", err);
      setRemoveError(err?.message || "An unexpected error occurred");
    } finally {
      setIsRemovingMember(false);
    }
  };

  // Load pending membership requests
  const loadRequests = useCallback(async () => {
    if (!slug) return;
    try {
      setLoadingRequests(true);
      const { data, error } = await api.listMembershipRequestsApiV1B2bSlugMembershipRequestsGet({
        path: { slug },
      });
      if (data && data.requests) {
        setPendingRequests(data.requests);
      } else if (error) {
        console.error("Failed to load membership requests:", error);
      }
    } catch (err) {
      console.error("Failed to load membership requests:", err);
    } finally {
      setLoadingRequests(false);
    }
  }, [slug]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Filtered membership requests
  const filteredRequests = useMemo(() => {
    return pendingRequests.filter((req) => {
      const matchesSearch =
        searchQuery.trim() === "" ||
        (req.name && req.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (req.email && req.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (req.role && req.role.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (req.job_title && req.job_title.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesDept =
        selectedDeptFilter === "All" ||
        (req.department && req.department.toLowerCase() === selectedDeptFilter.toLowerCase());

      return matchesSearch && matchesDept;
    });
  }, [pendingRequests, searchQuery, selectedDeptFilter]);

  const handleApproveRequest = async (userId: number, workspaceId?: number) => {
    if (!slug) return;
    try {
      setProcessingRequestId(userId);
      const { data, error } =
        await api.approveMembershipRequestApiV1B2bSlugMembershipRequestsTargetUserIdApprovePost({
          path: { slug, target_user_id: userId },
          body: { workspace_id: workspaceId },
        });
      if (data) {
        setRequestToApprove(null);
        await loadRequests();
        await loadMembers(true);
        if (loadStats) await loadStats(true);
      } else {
        alert(`Failed to approve request: ${getErrorMessage(error, "Unknown error")}`);
      }
    } catch (err) {
      console.error("Failed to approve request:", err);
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleRejectRequest = (userId: number) => {
    if (!slug) return;
    const request = pendingRequests.find((item) => item.user_id === userId);
    setConfirmAction({
      title: "Reject membership request?",
      description: `Reject ${request?.name || request?.email || "this user's"} request to join ${stats.organization_name || "the organization"}? They will be notified by email.`,
      confirmLabel: "Reject request",
      onConfirm: async () => {
        try {
          setProcessingRequestId(userId);
          const { data, error } =
            await api.rejectMembershipRequestApiV1B2bSlugMembershipRequestsTargetUserIdRejectPost({
              path: { slug, target_user_id: userId },
            });
          if (data) {
            await loadRequests();
            if (loadStats) await loadStats(true);
          } else {
            alert(`Failed to reject request: ${getErrorMessage(error, "Unknown error")}`);
          }
        } catch (err) {
          console.error("Failed to reject request:", err);
        } finally {
          setProcessingRequestId(null);
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <ConfirmActionModal action={confirmAction} onClose={() => setConfirmAction(null)} />
      {/* Department Breakdown Mini Cards */}
      {stats.department_stats && stats.department_stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {stats.department_stats.map((ds) => {
            const isSelected = selectedDeptFilter === ds.department;
            return (
              <button
                key={ds.department}
                onClick={() => setSelectedDeptFilter(isSelected ? "All" : ds.department)}
                className={`p-3.5 rounded-2xl border text-left transition shadow-xs flex flex-col justify-between ${
                  isSelected
                    ? "bg-indigo-50/80 border-indigo-300 ring-2 ring-indigo-500/20 text-indigo-900"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:shadow-sm"
                }`}
              >
                <div className="text-[11px] font-bold truncate flex items-center gap-1.5 text-slate-700">
                  <Building2 className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                  <span className="truncate">{ds.department}</span>
                </div>
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-xl font-black text-slate-900">{ds.total_members}</span>
                  <span className="text-[10px] text-slate-400 font-medium">Learners</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Sub-tabs & Action Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-1.5 bg-slate-100/90 p-1.5 rounded-2xl border border-slate-200">
          <button
            onClick={() => setMembersSubTab("members")}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
              membersSubTab === "members"
                ? "bg-white text-indigo-700 shadow-xs font-extrabold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            Active Employees ({members.length})
          </button>
          <button
            onClick={() => setMembersSubTab("requests")}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
              membersSubTab === "requests"
                ? "bg-white text-indigo-700 shadow-xs font-extrabold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <UserCheck className="w-3.5 h-3.5" />
            Membership Requests
            {pendingRequests.length > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-mono font-bold rounded-full animate-pulse">
                {pendingRequests.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setMembersSubTab("invites")}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
              membersSubTab === "invites"
                ? "bg-white text-indigo-700 shadow-xs font-extrabold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Mail className="w-3.5 h-3.5" />
            Pending Invitations
            {invitations.length > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-mono font-bold rounded-full">
                {invitations.length}
              </span>
            )}
          </button>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 transition shadow-sm w-full sm:w-auto active:scale-95"
          >
            <Mail className="w-3.5 h-3.5" />
            Invite Member
          </button>
          <button
            onClick={() => setShowBulkModal(true)}
            className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 transition border border-slate-200 shadow-xs w-full sm:w-auto"
          >
            <UploadCloud className="w-3.5 h-3.5 text-indigo-600" />
            Bulk CSV Upload
          </button>
        </div>
      </div>

      {/* Search & Department Filter Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full sm:w-auto flex-1">
          <div className="relative w-full sm:max-w-xs">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={
                membersSubTab === "members"
                  ? "Search employees by name, email, role..."
                  : membersSubTab === "requests"
                  ? "Search requests by name, email, role..."
                  : "Search invitations by email, role..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-xs"
            />
          </div>

          <select
            value={selectedDeptFilter}
            onChange={(e) => setSelectedDeptFilter(e.target.value)}
            className="px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-700 font-semibold focus:outline-none focus:border-indigo-500 shadow-xs"
          >
            <option value="All">All Departments</option>
            {(stats.departments || []).map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>

        <span className="text-xs text-slate-500 font-medium">
          {membersSubTab === "members"
            ? `Showing ${filteredMembers.length} of ${members.length} active members`
            : membersSubTab === "requests"
            ? `Showing ${filteredRequests.length} of ${pendingRequests.length} pending requests`
            : `Showing ${filteredInvitations.length} of ${invitations.length} pending invitations`}
        </span>
      </div>

      {/* Early-Warning At-Risk Filter Pills */}
      {membersSubTab === "members" && (
        <div className="flex flex-wrap items-center gap-2 pt-1 pb-1">
          <button
            type="button"
            onClick={() => setStatusFilterPill("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition flex items-center space-x-1.5 ${
              statusFilterPill === "all"
                ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-neutral-800 dark:text-neutral-300"
            }`}
          >
            <span>All Members</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-slate-200/60 dark:bg-neutral-700">
              {members.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilterPill("gaps")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition flex items-center space-x-1.5 ${
              statusFilterPill === "gaps"
                ? "bg-amber-600 text-white"
                : "bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200/80"
            }`}
          >
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            <span>Gaps Detected</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-amber-200 text-amber-900 font-bold">
              {atRiskCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilterPill("overdue")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition flex items-center space-x-1.5 ${
              statusFilterPill === "overdue"
                ? "bg-rose-600 text-white"
                : "bg-rose-50 text-rose-800 hover:bg-rose-100 border border-rose-200/80"
            }`}
          >
            <Clock className="w-3 h-3 text-rose-500" />
            <span>Overdue / At Risk</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-rose-200 text-rose-900 font-bold">
              {overdueCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilterPill("deadline")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition flex items-center space-x-1.5 ${
              statusFilterPill === "deadline"
                ? "bg-yellow-600 text-white"
                : "bg-yellow-50 text-yellow-800 hover:bg-yellow-100 border border-yellow-200/80"
            }`}
          >
            <Clock className="w-3 h-3 text-yellow-600" />
            <span>Approaching Deadline</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-yellow-200 text-yellow-900 font-bold">
              {deadlineCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilterPill("top_performer")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition flex items-center space-x-1.5 ${
              statusFilterPill === "top_performer"
                ? "bg-emerald-600 text-white"
                : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200/80"
            }`}
          >
            <TrendingUp className="w-3 h-3 text-emerald-500" />
            <span>Top Performers</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-200 text-emerald-900 font-bold">
              {topPerformerCount}
            </span>
          </button>
        </div>
      )}

      {/* Active Members Table */}
      {membersSubTab === "members" && (
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Role &amp; Department</th>
                  <th className="px-6 py-4">Status &amp; Intelligence</th>
                  <th className="px-6 py-4 text-center">Enrolled</th>
                  <th className="px-6 py-4 text-center">Completed</th>
                  <th className="px-6 py-4 text-center">Quizzes Passed</th>
                  <th className="px-6 py-4">Joined Date</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                      {members.length === 0 ? (
                        <div>
                          <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                          <p className="font-bold text-slate-700 text-sm">No active members enrolled yet.</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Click &quot;Invite Member&quot; above or share your B2B Join Link.
                          </p>
                        </div>
                      ) : (
                        "No matching employees found for the selected filters."
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((m) => {
                    const isCurrentUser = Boolean(
                      clerkUser &&
                        (m.clerk_user_id === clerkUser.id ||
                          (!m.clerk_user_id && m.email === clerkUser.primaryEmailAddress?.emailAddress))
                    );
                    const resolvedName =
                      (m.name && m.name !== "Employee" ? m.name : null) ||
                      (isCurrentUser ? clerkUser?.fullName : null) ||
                      (m.name ? m.name : null) ||
                      (m.email ? m.email.split("@")[0] : null) ||
                      "Employee";
                    const resolvedEmail =
                      m.email ||
                      (isCurrentUser ? clerkUser?.primaryEmailAddress?.emailAddress : null);

                    const initials = resolvedName
                      .split(" ")
                      .filter(Boolean)
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase() || "E";

                    const isSoleAdmin = m.role?.toLowerCase() === "admin" && adminCount <= 1;

                    return (
                      <tr key={m.user_id} className="hover:bg-slate-50/80 transition group">
                        <td className="px-6 py-4">
                          <div
                            className="flex items-center gap-3 cursor-pointer group-hover:text-indigo-600 transition"
                            onClick={() => setSelectedDossierMember({ ...m, name: resolvedName, email: resolvedEmail })}
                            title="Click to view 1-on-1 Review Assistant & Competency Dossier"
                          >
                            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-indigo-700 text-white font-bold text-xs flex items-center justify-center shadow-xs flex-shrink-0">
                              {initials}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition flex items-center gap-1.5">
                                <span>{resolvedName}</span>
                                <BrainCircuit className="w-3.5 h-3.5 text-violet-500 opacity-0 group-hover:opacity-100 transition" />
                              </div>
                              <div className="text-[11px] text-slate-500 font-mono">{resolvedEmail || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-0.5 border font-bold rounded text-[10px] uppercase tracking-wider ${
                                m.role === "admin"
                                  ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                                  : m.role === "manager"
                                  ? "bg-purple-50 border-purple-200 text-purple-700"
                                  : "bg-slate-100 border-slate-200 text-slate-700"
                              }`}
                            >
                              {m.role}
                            </span>
                            <span className="text-slate-600 text-xs font-medium">
                              {m.department || "General"}
                            </span>
                          </div>
                          {m.job_title && (
                            <div className="text-[11px] text-slate-500 mt-0.5">{m.job_title}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1 items-start">
                            {m.status_pill === "top_performer" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <TrendingUp className="w-3 h-3" />
                                Top Performer
                              </span>
                            ) : m.status_pill === "overdue" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                <Clock className="w-3 h-3" />
                                Overdue ({m.overdue_count || 1})
                              </span>
                            ) : m.status_pill === "gaps_detected" || (m.gaps_count && m.gaps_count > 0) ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                <AlertTriangle className="w-3 h-3" />
                                {m.gaps_count || 1} Skill Gap{m.gaps_count === 1 ? "" : "s"}
                              </span>
                            ) : m.status_pill === "approaching_deadline" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-yellow-50 text-yellow-700 border border-yellow-200">
                                <Clock className="w-3 h-3" />
                                Due Soon
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                On Track
                              </span>
                            )}
                            {typeof m.avg_quiz_score === "number" && m.avg_quiz_score > 0 && (
                              <span className="text-[10px] text-slate-400 font-medium">
                                {Math.round(m.avg_quiz_score)}% avg quiz score
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center font-bold text-slate-800">
                          {m.courses_enrolled}
                        </td>
                        <td className="px-6 py-4 text-center font-bold text-emerald-600">
                          <span className="inline-flex items-center gap-1 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                            {m.courses_completed}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center font-bold text-purple-600">
                          <span className="inline-flex items-center gap-1 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100">
                            <Sparkles className="w-3 h-3" />
                            {m.quizzes_passed}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-500 text-xs">
                          {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => setSelectedDossierMember(m)}
                              className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition"
                              title="Open Employee Dossier & AI Briefing"
                            >
                              <BrainCircuit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setRemoveError(null);
                                setMemberToRemove(m);
                              }}
                              disabled={isSoleAdmin}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400"
                              title={
                                isSoleAdmin
                                  ? "Cannot remove the organization's sole administrator"
                                  : "Remove member from organization"
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Membership Requests Table */}
      {membersSubTab === "requests" && (
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-6 py-4">Requester</th>
                  <th className="px-6 py-4">Role &amp; Department</th>
                  <th className="px-6 py-4">Experience &amp; Goals</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Requested Date</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      {pendingRequests.length === 0 ? (
                        <div>
                          <UserCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                          <p className="font-bold text-slate-700 text-sm">No membership requests pending.</p>
                          <p className="text-xs text-slate-500 mt-1">
                            When learners sign up through your invitation link, their requests will appear here for approval.
                          </p>
                        </div>
                      ) : (
                        "No matching membership requests found."
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((req) => {
                    const displayName = req.name || req.email || "Requester";
                    const initials = displayName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase();
                    const isProcessing = processingRequestId === req.user_id;

                    return (
                      <tr key={req.user_id} className="hover:bg-slate-50/80 transition group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-amber-500 to-indigo-600 text-white font-bold text-xs flex items-center justify-center shadow-xs">
                              {initials}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900">{req.name || "New Learner"}</div>
                              <div className="text-[11px] text-slate-500 font-mono">{req.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 font-semibold rounded text-[10px] uppercase">
                              {req.role || "Member"}
                            </span>
                            <span className="text-slate-600 text-xs font-medium">
                              {req.department || "General"}
                            </span>
                          </div>
                          {req.job_title && (
                            <div className="text-[11px] text-slate-500 mt-0.5">{req.job_title}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {req.experience_level && (
                              <span className="inline-block px-2 py-0.5 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded text-[10px] font-semibold">
                                {req.experience_level}
                              </span>
                            )}
                            {req.learning_goals && req.learning_goals.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {req.learning_goals.slice(0, 3).map((goal, idx) => (
                                  <span
                                    key={idx}
                                    className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded font-medium"
                                  >
                                    {goal}
                                  </span>
                                ))}
                                {req.learning_goals.length > 3 && (
                                  <span className="text-[10px] text-slate-400">
                                    +{req.learning_goals.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                            Pending Approval
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-500 text-xs">
                          {req.requested_at ? new Date(req.requested_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setRequestToApprove(req)}
                              disabled={isProcessing}
                              className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 shadow-sm transition active:scale-95"
                              title="Approve membership and choose workspace"
                            >
                              <Check className="w-3.5 h-3.5" />
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => handleRejectRequest(req.user_id)}
                              disabled={isProcessing}
                              className="px-3.5 py-1.5 bg-white hover:bg-rose-50 disabled:opacity-50 text-rose-600 border border-rose-200 font-bold text-xs rounded-xl flex items-center gap-1.5 transition shadow-xs"
                              title="Reject membership request"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Reject</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Invitations Table */}
      {membersSubTab === "invites" && (
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-6 py-4">Invited Email</th>
                  <th className="px-6 py-4">Role &amp; Department</th>
                  <th className="px-6 py-4">Target Workspace</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Sent Date</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredInvitations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      {invitations.length === 0 ? (
                        <div>
                          <Mail className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                          <p className="font-bold text-slate-700 text-sm">No pending invitations.</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Click &quot;Invite Member&quot; above to invite new colleagues.
                          </p>
                        </div>
                      ) : (
                        "No matching invitations found."
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredInvitations.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-50/80 transition">
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-900 font-mono">{inv.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 font-semibold rounded text-[10px] uppercase">
                            {inv.role}
                          </span>
                          {inv.department && (
                            <span className="text-slate-600 text-xs">{inv.department}</span>
                          )}
                        </div>
                        {inv.job_title && (
                          <div className="text-[11px] text-slate-500 mt-0.5">{inv.job_title}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 border border-indigo-200 text-indigo-700 text-[11px] font-semibold rounded-lg">
                          <Layers className="w-3 h-3 text-indigo-500" />
                          {inv.workspace_name || "General (Default)"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                          Pending Acceptance
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-500 text-xs">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleCopyInviteLink(inv)}
                            className="px-3 py-1.5 bg-white hover:bg-slate-50 text-indigo-600 border border-slate-200 rounded-lg font-semibold text-xs flex items-center gap-1 shadow-xs transition"
                            title="Copy direct invitation accept link"
                          >
                            {copiedInviteId === inv.id ? (
                              <>
                                <Check className="w-3 h-3 text-emerald-600" />
                                <span className="text-emerald-600 font-bold">Copied</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                <span>Copy Link</span>
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleResendInvite(inv.id)}
                            disabled={resendingInviteId === inv.id}
                            className="px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-lg font-semibold text-xs flex items-center gap-1 transition disabled:opacity-50"
                            title="Resend email invitation"
                          >
                            <RefreshCw
                              className={`w-3 h-3 ${resendingInviteId === inv.id ? "animate-spin text-indigo-600" : ""}`}
                            />
                            <span>Resend</span>
                          </button>
                          <button
                            onClick={() => handleRevokeInvite(inv.id)}
                            disabled={revokingInviteId === inv.id}
                            className="px-3 py-1.5 bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 rounded-lg font-semibold text-xs flex items-center gap-1 transition disabled:opacity-50"
                            title="Revoke invitation"
                          >
                            <Trash2 className="w-3 h-3" />
                            <span>Revoke</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Instances */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        slug={slug}
        departments={stats.departments || []}
        workspaces={workspaces}
        onSuccess={async () => {
          await loadMembers(true);
          await loadRequests();
          await loadInvites(true);
          if (loadStats) await loadStats(true);
        }}
      />

      <BulkInviteModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        slug={slug}
        workspaces={workspaces}
        onSuccess={async () => {
          await loadMembers(true);
          await loadRequests();
          await loadInvites(true);
          if (loadStats) await loadStats(true);
          setMembersSubTab("invites");
        }}
      />

      <ApproveRequestModal
        isOpen={Boolean(requestToApprove)}
        onClose={() => setRequestToApprove(null)}
        request={requestToApprove}
        workspaces={workspaces}
        isProcessing={processingRequestId !== null}
        onApprove={handleApproveRequest}
      />

      {/* Remove Member Confirmation Modal */}
      <Modal
        isOpen={!!memberToRemove}
        onClose={() => {
          if (!isRemovingMember) {
            setMemberToRemove(null);
            setRemoveError(null);
          }
        }}
        title="Remove Organization Member"
        subtitle="This action will revoke the user's access to the organization."
        icon={<Trash2 className="w-5 h-5 text-rose-600" />}
      >
        <div className="space-y-4">
          {removeError && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
              {removeError}
            </div>
          )}

          <p className="text-xs text-slate-600 leading-relaxed">
            Are you sure you want to remove{" "}
            <span className="font-bold text-slate-900">
              {memberToRemove?.name || memberToRemove?.email || "this employee"}
            </span>{" "}
            from <span className="font-semibold text-slate-800">{stats.organization_name || "the organization"}</span>?
          </p>

          <div className="p-3.5 bg-amber-50/70 border border-amber-200/80 rounded-2xl text-xs text-amber-800 space-y-1">
            <p className="font-bold text-amber-900">Please note:</p>
            <ul className="list-disc list-inside space-y-0.5 text-[11px] text-amber-800">
              <li>They will immediately lose access to the organization portal.</li>
              <li>They will be removed from all workspaces and cohorts.</li>
              <li>Direct course assignments will be unassigned.</li>
              <li>Existing learning progress and certificates will remain preserved.</li>
            </ul>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                setMemberToRemove(null);
                setRemoveError(null);
              }}
              disabled={isRemovingMember}
              className="px-4 py-2 border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl hover:bg-slate-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmRemoveMember}
              disabled={isRemovingMember}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-xl transition shadow-xs flex items-center gap-1.5 disabled:opacity-50"
            >
              {isRemovingMember ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Removing...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Remove Member</span>
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Employee Dossier & AI Briefing Drawer */}
      <EmployeeDossierDrawer
        isOpen={!!selectedDossierMember}
        onClose={() => setSelectedDossierMember(null)}
        orgSlug={slug}
        userId={selectedDossierMember?.user_id || null}
        memberName={selectedDossierMember?.name}
        memberEmail={selectedDossierMember?.email}
      />
    </div>
  );
};
